/**
 * 행 컬렉션 등록형(registered) 도메인 위젯 — 위험도 추정(risk-estimation).
 *
 * 서버 조립이 방법론별로 값 객체를 준다(report-composer.service assembleInspectionItems):
 *   - FREQUENCY_SEVERITY: { likelihood: number; severity: number }  (레벨 값, 예 2·3)
 *   - THREE_LEVEL:        { level: string }                          (라벨, 예 '하'·'중'·'상')
 *
 * DLR 상세 셀은 방법론 switch format으로 표시한다
 *   (THREE_LEVEL→`{riskEstimation.level}`, FREQUENCY_SEVERITY→`{riskEstimation.likelihood}x{riskEstimation.severity}`).
 * 작성폼 행 그리드(RowsCollectionEditor)엔 그 포맷터가 없어, 위젯 미등록 시 readonly 폴백이
 * String(객체)=**[object Object]**로 표시되던 결함 D2를 이 위젯이 사람이 읽을 문자열로 대체한다.
 *
 * 편집형(구 SMS RiskAssessmentStep 동형): editable + host 주입 context({ values, resolvedRows })에서
 * 기준표(risk-assessment-config 단수 entry — resolvedRows 우선 → values 폴백, resolveDynamicSelectRows와
 * 동형 우선순위)를 해석할 수 있으면
 *   - FREQUENCY_SEVERITY: 빈도·강도 select 2개(옵션 `{label} ({level})`) — 변경 시 shared
 *     scoreToRiskLevel로 levelLabel·levelColor 즉시 재계산해 값 객체에 갱신(서버 동봉 필드와 동일
 *     이름 — stale 방지, 저장·지면 포맷 일관). 수준 배지는 기존 표시 재사용(라이브 값 기준).
 *   - THREE_LEVEL: levelCriteria 라벨 버튼 선택(구 SMS 동형 — 상=red/중=yellow/하=green 결).
 * 기준표 해석은 이 위젯의 도메인 책임(그리드는 불투명 통로 — RegisteredRowWidget.context 계약).
 *
 * editable=false 또는 기준표 미가용(미주입 host·미지원 방식·기준 부재)이면 종전 **표시 전용** 그대로:
 * 표시는 `가능성×중대성 = 곱`(DLR 상세와 동형 + 곱 병기)에 levelLabel 있으면 수준 배지 병기
 * (방어적으로 절대 [object Object] 미표시). 재계산 불가(리스크 기준 미구성 config)면 서버 결과 결로
 * levelLabel 생략(입력 변경 시 stale 산출 필드는 항상 제거).
 */
import type { CSSProperties } from 'react';
import { scoreToRiskLevel } from '@safetyhub/shared';
import type { RiskAssessmentConfig } from '@safetyhub/shared';
import type { RegisteredRowWidget } from '../RowsCollectionEditor';

interface RiskEstimationValue {
  /** THREE_LEVEL: 위험도 레벨 라벨(예 '하'·'중'·'상') */
  level?: string;
  /** FREQUENCY_SEVERITY: 가능성 레벨 값 */
  likelihood?: number;
  /** FREQUENCY_SEVERITY: 중대성 레벨 값 */
  severity?: number;
  /** FREQUENCY_SEVERITY: 산출 위험성 수준 라벨(예 '높음' — riskLevelCriteria 환산, 서버 동봉/편집 재계산) */
  levelLabel?: string;
  /** levelLabel 색(criteria color — hex 또는 색 이름). 없으면 라벨 관례색 fallback */
  levelColor?: string;
}

/** 불명확한 값을 위험도 값 객체로 정규화(객체가 아니면 빈 객체). */
function asValue(value: unknown): RiskEstimationValue {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as RiskEstimationValue;
  }
  return {};
}

// ── 기준표(context) 해석 — 도메인 책임 ──────────────────────────────────────

/** 레벨형 기준 항목(likelihood/severity/levelCriteria 공통 최소 형상). */
interface LevelCriterion {
  level: number;
  label: string;
  color?: string;
}

/** 구간형 기준 항목(riskLevelCriteria — scoreToRiskLevel min/max 매칭 형상). */
interface RangeCriterion {
  min: number;
  max: number;
  label: string;
  level?: number;
  color?: string;
  managementAction?: string;
}

/** 위젯 편집 UI가 소비하는 기준표(서버 mapRiskAssessmentConfigResolved 산출에서 해석). */
export interface RiskEstimationCriteria {
  methodType: 'FREQUENCY_SEVERITY' | 'THREE_LEVEL';
  likelihoodCriteria: LevelCriterion[];
  severityCriteria: LevelCriterion[];
  riskLevelCriteria: RangeCriterion[];
  levelCriteria: LevelCriterion[];
}

/** 배열에서 {level:number,label:string} 형상 항목만 취한다(원본 객체 보존 — color 등 유지). */
function asLevelCriteria(v: unknown): LevelCriterion[] {
  if (!Array.isArray(v)) return [];
  return v.filter(
    (c): c is LevelCriterion =>
      !!c &&
      typeof c === 'object' &&
      typeof (c as { level?: unknown }).level === 'number' &&
      typeof (c as { label?: unknown }).label === 'string',
  );
}

/** 배열에서 {min,max:number,label:string} 형상 항목만 취한다(원본 객체 보존 — color/level 등 유지). */
function asRangeCriteria(v: unknown): RangeCriterion[] {
  if (!Array.isArray(v)) return [];
  return v.filter(
    (c): c is RangeCriterion =>
      !!c &&
      typeof c === 'object' &&
      typeof (c as { min?: unknown }).min === 'number' &&
      typeof (c as { max?: unknown }).max === 'number' &&
      typeof (c as { label?: unknown }).label === 'string',
  );
}

/**
 * host 주입 context({ values, resolvedRows })에서 위험성평가 기준표를 해석한다.
 *
 * entry 채널: resolvedRows['risk-assessment-config'] ?? values['risk-assessment-config']
 * (단수 entry — resolveDynamicSelectRows와 동형 우선순위. 배열로 실려와도 첫 항목으로 방어).
 * entry 형상: 서버 mapRiskAssessmentConfigResolved 산출 — flat 4키(likelihood/severity/riskLevel/
 * levelCriteria) + 합성 criteria 객체 + methodType/assessmentMethod. flat 우선, criteria 폴백.
 * 방식: methodType 우선 → assessmentMethod/method 폴백('THREE_LEVEL'=상중하, '3x3'/'5x5'=빈도강도).
 * 미지원 방식(CHECKLIST 등)·미해석이면 undefined — 호출부가 표시 전용으로 폴백한다.
 */
export function extractRiskEstimationCriteria(
  context: unknown,
): RiskEstimationCriteria | undefined {
  if (!context || typeof context !== 'object') return undefined;
  const { values, resolvedRows } = context as { values?: unknown; resolvedRows?: unknown };
  const entryFrom = (channel: unknown): unknown =>
    channel && typeof channel === 'object'
      ? (channel as Record<string, unknown>)['risk-assessment-config']
      : undefined;
  let entry = entryFrom(resolvedRows) ?? entryFrom(values);
  if (Array.isArray(entry)) entry = entry[0];
  if (!entry || typeof entry !== 'object') return undefined;

  const e = entry as Record<string, unknown>;
  const nested =
    e.criteria && typeof e.criteria === 'object' && !Array.isArray(e.criteria)
      ? (e.criteria as Record<string, unknown>)
      : undefined;
  const pick = (key: string): unknown => e[key] ?? nested?.[key];

  const likelihoodCriteria = asLevelCriteria(pick('likelihoodCriteria'));
  const severityCriteria = asLevelCriteria(pick('severityCriteria'));
  const riskLevelCriteria = asRangeCriteria(pick('riskLevelCriteria'));
  const levelCriteria = asLevelCriteria(pick('levelCriteria'));

  const rawMethod = [e.methodType, e.assessmentMethod, e.method].find(
    (m): m is string => typeof m === 'string',
  );
  let methodType: RiskEstimationCriteria['methodType'];
  if (rawMethod === 'THREE_LEVEL') {
    methodType = 'THREE_LEVEL';
  } else if (rawMethod === 'FREQUENCY_SEVERITY' || rawMethod === '3x3' || rawMethod === '5x5') {
    methodType = 'FREQUENCY_SEVERITY';
  } else if (
    rawMethod === undefined &&
    likelihoodCriteria.length > 0 &&
    severityCriteria.length > 0
  ) {
    // 방식값 미상(구세대 config) — 빈도·강도 기준이 서 있으면 기본 방식(scoreToRiskLevel 결).
    methodType = 'FREQUENCY_SEVERITY';
  } else {
    return undefined;
  }
  return { methodType, likelihoodCriteria, severityCriteria, riskLevelCriteria, levelCriteria };
}

/**
 * 레벨 값 → 1~10 정규화 점수 역변환(shared normalizedToLevel의 균등 분할 역함수).
 * scoreToRiskLevel이 1~10 점수를 기준표로 재밴딩하므로, 사용자가 고른 레벨을 그 레벨로
 * 되돌아오는 점수로 환산해 넘긴다(서버 산출과 동일 경로·동일 결과 보장).
 */
function levelToScore(level: number, criteria: LevelCriterion[]): number {
  const sorted = [...criteria].sort((a, b) => a.level - b.level);
  const n = sorted.length;
  if (n <= 1) return 1;
  let idx = sorted.findIndex((c) => c.level === level);
  // 기준표에 없는 레벨 값(구세대 데이터)은 1-based 순번 근사.
  if (idx < 0) idx = Math.max(0, Math.min(n - 1, Math.round(level) - 1));
  return 1 + (9 * idx) / (n - 1);
}

/**
 * FREQUENCY_SEVERITY 편집 1회분 적용(순수) — select 변경 핸들러의 값 산출.
 * 산출 필드(levelLabel/levelColor)는 입력이 바뀌는 순간 stale이므로 항상 제거 후,
 * 빈도·강도 모두 있고 riskLevelCriteria 구성 시에만 shared scoreToRiskLevel로 재계산해
 * 다시 동봉한다(서버 동봉 필드와 동일 이름 — 저장·지면 포맷 일관).
 */
export function applyFrequencySeverityEdit(
  value: unknown,
  field: 'likelihood' | 'severity',
  nextLevel: number | undefined,
  criteria: RiskEstimationCriteria,
): RiskEstimationValue {
  const next: RiskEstimationValue = { ...asValue(value) };
  delete next.levelLabel;
  delete next.levelColor;
  if (nextLevel === undefined) delete next[field];
  else next[field] = nextLevel;

  const lk = typeof next.likelihood === 'number' ? next.likelihood : undefined;
  const sv = typeof next.severity === 'number' ? next.severity : undefined;
  if (lk !== undefined && sv !== undefined && criteria.riskLevelCriteria.length > 0) {
    const scoringConfig = {
      methodType: 'FREQUENCY_SEVERITY',
      likelihoodCriteria: criteria.likelihoodCriteria,
      severityCriteria: criteria.severityCriteria,
      riskLevelCriteria: criteria.riskLevelCriteria,
    } as unknown as RiskAssessmentConfig;
    const result = scoreToRiskLevel(
      levelToScore(lk, criteria.likelihoodCriteria),
      levelToScore(sv, criteria.severityCriteria),
      scoringConfig,
    );
    if (result?.riskLabel) {
      next.levelLabel = result.riskLabel;
      if (result.color) next.levelColor = result.color;
    }
  }
  return next;
}

// ── 표시 전용(종전) ─────────────────────────────────────────────────────────

// RowsCollectionEditor의 readonly 셀 톤과 맞춘 표시 클래스.
// min-h-[2rem]는 admin CSS에 없던 유틸 — rc Tailwind 규약(임계 스타일 인라인)에 따라 style로 부여.
const readonlyValueClass =
  'w-full px-2 py-1.5 border border-gray-200 bg-gray-50 rounded text-sm text-gray-500 whitespace-pre-wrap break-words';
const readonlyValueStyle = { minHeight: '2rem' } as const;

// 수준 배지 관례색(구 SMS THREE_LEVEL_COLOR_MAP 결: 상=red/중=yellow/하=green).
// rc Tailwind 규약(임계 스타일 인라인)에 따라 전부 인라인 style 값으로 소유.
const LEVEL_BADGE_TONES = {
  red: { backgroundColor: '#fee2e2', color: '#b91c1c' },
  yellow: { backgroundColor: '#fef9c3', color: '#a16207' },
  green: { backgroundColor: '#dcfce7', color: '#15803d' },
  gray: { backgroundColor: '#f3f4f6', color: '#4b5563' },
} as const;

/** 라벨 패턴 → 관례 톤(상/높음/심각=red, 중/보통=yellow, 하/낮음=green, 그 외 gray). */
function toneKeyForLabel(label: string): keyof typeof LEVEL_BADGE_TONES {
  if (['높음', '심각', '상'].some((p) => label.includes(p))) return 'red';
  if (['보통', '중'].some((p) => label.includes(p))) return 'yellow';
  if (['낮음', '하'].some((p) => label.includes(p))) return 'green';
  return 'gray';
}

/**
 * 배지 색 결정: levelColor가 6자리 hex면 글자색=hex + 연한 배경(hex+알파),
 * 톤 이름(red/yellow/green)이면 해당 톤, 그 외/부재는 라벨 관례색 fallback.
 */
function badgeColors(
  label: string,
  levelColor?: string,
): { backgroundColor: string; color: string } {
  if (levelColor && /^#[0-9a-fA-F]{6}$/.test(levelColor)) {
    return { backgroundColor: `${levelColor}1f`, color: levelColor };
  }
  if (levelColor && levelColor in LEVEL_BADGE_TONES) {
    return LEVEL_BADGE_TONES[levelColor as keyof typeof LEVEL_BADGE_TONES];
  }
  return LEVEL_BADGE_TONES[toneKeyForLabel(label)];
}

/**
 * 위험도 표시 텍스트:
 *  - level(문자열)이 있으면 그 라벨(THREE_LEVEL).
 *  - likelihood·severity(숫자)가 있으면 `가능성×중대성 = 곱`(FREQUENCY_SEVERITY, DLR 상세와 동형 + 곱 병기).
 *  - 그 외(빈/이상 값) → '-' (절대 [object Object] 금지).
 */
function summarize(v: RiskEstimationValue): string {
  const level = typeof v.level === 'string' ? v.level.trim() : '';
  if (level !== '') return level;

  const lk = typeof v.likelihood === 'number' ? v.likelihood : undefined;
  const sv = typeof v.severity === 'number' ? v.severity : undefined;
  if (lk !== undefined && sv !== undefined) return `${lk}×${sv} = ${lk * sv}`;
  if (lk !== undefined) return `${lk}`;
  if (sv !== undefined) return `${sv}`;

  return '-';
}

/** 종전 표시 전용 렌더(곱/라벨 + 수준 배지) — 편집형의 라이브 요약으로도 재사용. */
function ReadonlyDisplay({ v }: { v: RiskEstimationValue }) {
  // 수준 배지는 FREQUENCY_SEVERITY(곱 표시) 전용 — THREE_LEVEL(level 라벨)은 기존 표시 무변.
  const levelLabel = typeof v.levelLabel === 'string' ? v.levelLabel.trim() : '';
  const isThreeLevel = typeof v.level === 'string' && v.level.trim() !== '';
  const showBadge = levelLabel !== '' && !isThreeLevel;
  return (
    <div className={readonlyValueClass} style={readonlyValueStyle}>
      {summarize(v)}
      {showBadge && (
        <span
          style={{
            ...badgeColors(
              levelLabel,
              typeof v.levelColor === 'string' ? v.levelColor : undefined,
            ),
            display: 'inline-block',
            marginLeft: '0.375rem',
            padding: '0.0625rem 0.375rem',
            borderRadius: '9999px',
            fontSize: '0.75rem',
            fontWeight: 600,
            lineHeight: '1rem',
          }}
        >
          {levelLabel}
        </span>
      )}
    </div>
  );
}

// ── 편집형 ──────────────────────────────────────────────────────────────────

// 그리드 편집 입력과 같은 톤(RowsCollectionEditor cellInputClass 동형 — 앱 공용 유틸만).
const selectInputClass =
  'w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500';
// 빈도/강도 2열 — rc 전용일 수 있는 grid-cols 유틸 대신 임계 레이아웃 인라인 소유.
const editPairStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '0.75rem',
};

/** 비활성(읽기전용) select 시각 — 네이티브 disabled에 회색 배경/텍스트 명시(브라우저 편차 방어, 인라인). */
const disabledControlStyle: CSSProperties = {
  backgroundColor: '#f9fafb',
  color: '#6b7280',
  cursor: 'default',
};

/** 빈도·강도 select 1개(옵션 `{label} ({level})`, 값 없으면 '선택') — 구 SMS 동형. disabled=읽기전용. */
function LevelSelect({
  label,
  criteria,
  current,
  onSelect,
  disabled,
}: {
  label: string;
  criteria: LevelCriterion[];
  current: number | undefined;
  onSelect: (next: number | undefined) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <select
        className={selectInputClass}
        style={disabled ? disabledControlStyle : undefined}
        disabled={disabled}
        value={typeof current === 'number' ? String(current) : ''}
        onChange={(e) =>
          onSelect(e.target.value === '' ? undefined : Number(e.target.value))
        }
      >
        <option value="">선택</option>
        {criteria.map((c) => (
          <option key={c.level} value={String(c.level)}>
            {c.label} ({c.level})
          </option>
        ))}
      </select>
    </div>
  );
}

/** FREQUENCY_SEVERITY: 빈도·강도 select 2개. disabled=읽기전용(값 표시·비활성, 곱/수준 요약 줄 없음). */
function FrequencySeverityEditor({
  value,
  criteria,
  onChange,
  disabled,
}: {
  value: unknown;
  criteria: RiskEstimationCriteria;
  onChange: (next: unknown) => void;
  disabled?: boolean;
}) {
  const v = asValue(value);
  return (
    <div style={editPairStyle}>
      <LevelSelect
        label="빈도 (가능성)"
        criteria={criteria.likelihoodCriteria}
        current={typeof v.likelihood === 'number' ? v.likelihood : undefined}
        onSelect={(next) => onChange(applyFrequencySeverityEdit(value, 'likelihood', next, criteria))}
        disabled={disabled}
      />
      <LevelSelect
        label="강도 (중대성)"
        criteria={criteria.severityCriteria}
        current={typeof v.severity === 'number' ? v.severity : undefined}
        onSelect={(next) => onChange(applyFrequencySeverityEdit(value, 'severity', next, criteria))}
        disabled={disabled}
      />
    </div>
  );
}

/** THREE_LEVEL: levelCriteria 라벨 버튼 선택(구 SMS 동형). disabled=읽기전용(선택값 표시·비활성). */
function ThreeLevelEditor({
  value,
  criteria,
  onChange,
  disabled,
}: {
  value: unknown;
  criteria: RiskEstimationCriteria;
  onChange: (next: unknown) => void;
  disabled?: boolean;
}) {
  const v = asValue(value);
  const current = typeof v.level === 'string' ? v.level : '';
  return (
    <div className="flex gap-2">
      {[...criteria.levelCriteria]
        .sort((a, b) => b.level - a.level)
        .map((l) => {
          const isSelected = current === l.label;
          const tone = badgeColors(l.label, l.color);
          return (
            <button
              key={l.level}
              type="button"
              disabled={disabled}
              // 구 SMS 동형 값 형상 — 전체 교체 { level: 라벨 } (THREE_LEVEL 값 객체 정본).
              onClick={disabled ? undefined : () => onChange({ level: l.label })}
              className={
                'flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors' +
                (isSelected ? '' : ' border-gray-200 text-gray-500') +
                (disabled ? ' cursor-default' : isSelected ? '' : ' hover:bg-gray-50')
              }
              // 선택 색은 기준 color/관례 톤 — rc Tailwind 규약(임계 스타일 인라인)에 따라 style 소유.
              style={isSelected ? { ...tone, borderColor: tone.color } : undefined}
            >
              {l.label}
            </button>
          );
        })}
    </div>
  );
}

const RiskEstimationWidget: RegisteredRowWidget = ({ value, onChange, editable, context }) => {
  // 기준표 해석은 editable 무관 — 읽기전용도 같은 컨트롤을 disabled로 렌더(값 표시 일관, 2026-07-20
  // 사용자 확정: readonly면 드롭다운이 사라지던 것을 비활성 드롭다운으로 통일). 기준표 미해석
  // (미주입 host·미지원 방식·기준 부재)만 표시 전용 텍스트 폴백(절대 [object Object] 금지, 회귀 0).
  const criteria = extractRiskEstimationCriteria(context);
  if (
    criteria?.methodType === 'FREQUENCY_SEVERITY' &&
    criteria.likelihoodCriteria.length > 0 &&
    criteria.severityCriteria.length > 0
  ) {
    return (
      <FrequencySeverityEditor
        value={value}
        criteria={criteria}
        onChange={onChange}
        disabled={!editable}
      />
    );
  }
  if (criteria?.methodType === 'THREE_LEVEL' && criteria.levelCriteria.length > 0) {
    return (
      <ThreeLevelEditor value={value} criteria={criteria} onChange={onChange} disabled={!editable} />
    );
  }
  return <ReadonlyDisplay v={asValue(value)} />;
};

export default RiskEstimationWidget;
